import { AndroidWalletFields, IosPosterFields, IosWalletFields, Program, WalletConfig, WalletFieldConfig } from "../../services/api";

// What the store needs of the loyalty card's front texts, kept apart from the preview (`wallet-front-fields.ts`),
// whose template resolver brings date-fns: the selectors are in the initial bundle.

/** The front slots of each platform, in the order the pass shows them. */
export const IOS_FRONT_SLOTS = ["header", "secondary1", "secondary2", "secondary3", "secondary4"] as const satisfies readonly (keyof IosWalletFields)[];
/** The iOS poster's own texts (`wallet.ios.poster.fields`); its header and main fields are the store card's. */
export const IOS_POSTER_FRONT_SLOTS = ["footer"] as const satisfies readonly (keyof IosPosterFields)[];
/** Apple's row below the strip: Wallet spreads the shown ones across it, in this order, up to four. */
export const IOS_SECONDARY_SLOTS = ["secondary1", "secondary2", "secondary3", "secondary4"] as const satisfies readonly IosFrontSlot[];
export const ANDROID_FRONT_SLOTS = ["row1Start", "row1End", "row2Start", "row2End"] as const satisfies readonly (keyof AndroidWalletFields)[];

export type IosFrontSlot = (typeof IOS_FRONT_SLOTS)[number];
export type IosSecondarySlot = (typeof IOS_SECONDARY_SLOTS)[number];
export type IosPosterFrontSlot = (typeof IOS_POSTER_FRONT_SLOTS)[number];
export type AndroidFrontSlot = (typeof ANDROID_FRONT_SLOTS)[number];
/** Where front texts are configured: each wallet's `fields`, and the iOS poster's own (as `frontFields` in the metadata). */
export type WalletFrontPlatform = "ios" | "iosPoster" | "android";
export type WalletFrontSlot = IosFrontSlot | IosPosterFrontSlot | AndroidFrontSlot;
/** The parts of a front slot that hold text, and so take templates and translations (not its `hidden` switch). */
export type WalletFieldPart = "label" | "value";

/** Translations of the configured front texts live in `entity_translations` under this type, without an entity. */
export const WALLET_FIELD_ENTITY_TYPE = "wallet_field";

// Google takes one string for every language in the values of the first row (loyaltyPoints).
const UNTRANSLATABLE_PARTS = new Set(["android.row1Start.value", "android.row1End.value"]);

export const FRONT_SLOTS: Record<WalletFrontPlatform, readonly WalletFrontSlot[]> = {
  ios: IOS_FRONT_SLOTS,
  iosPoster: IOS_POSTER_FRONT_SLOTS,
  android: ANDROID_FRONT_SLOTS,
};
// Each platform's place in the wallet config, which is also the prefix of its translation paths (`ios.poster.footer.label`)
const PLATFORM_PATHS: Record<WalletFrontPlatform, string> = { ios: "ios", iosPoster: "ios.poster", android: "android" };
const PARTS: readonly WalletFieldPart[] = ["label", "value"];

export type FieldsOf = Partial<Record<WalletFrontSlot, WalletFieldConfig>>;

export const fieldsOf = (wallet: Partial<WalletConfig> | undefined, platform: WalletFrontPlatform): FieldsOf =>
  ((platform === "iosPoster" ? wallet?.ios?.poster?.fields : wallet?.[platform]?.fields) ?? {});

/** The config path of a platform's fields, as the template metadata lists them (`wallet.ios.poster.fields`). */
export const walletFieldsConfigPath = (platform: WalletFrontPlatform): string => `wallet.${PLATFORM_PATHS[platform]}.fields`;

/** Whether Google can show a translation of this part (`android.row1Start.value` cannot). */
export const isWalletFieldPartTranslatable = (platform: WalletFrontPlatform, slot: WalletFrontSlot, part: WalletFieldPart): boolean =>
  !UNTRANSLATABLE_PARTS.has(`${platform}.${slot}.${part}`);

/** Where a part's translations live: the tier's own path when the tier overrides that part, else the program's (as the API reads them). */
export const walletFieldTranslationPath = (
  program: Program,
  tier: string | undefined,
  platform: WalletFrontPlatform,
  slot: WalletFrontSlot,
  part: WalletFieldPart,
): string => {
  const path = `${PLATFORM_PATHS[platform]}.${slot}.${part}`;
  const tierWallet = tier ? (program.configurations.tiers?.[tier] as Partial<WalletConfig> | undefined) : undefined;
  return fieldsOf(tierWallet, platform)[slot]?.[part] !== undefined ? `tiers.${tier}.${path}` : path;
};

/**
 * Where a title's translations live. Apple's: the tier's own when the tier has its own title, else the program's.
 * Google's title is the class's program name, one for every tier, so it is always the program's (plain text).
 */
export const walletTitleTranslationPath = (program: Program, tier: string | undefined, platform: "ios" | "android"): string => {
  const tierWallet = tier ? (program.configurations.tiers?.[tier] as Partial<WalletConfig> | undefined) : undefined;
  return platform === "ios" && tierWallet?.ios?.title !== undefined ? `tiers.${tier}.ios.title` : `${platform}.title`;
};

/** Every configured text that can be translated, with its text in the program's language: the program's, then each tier's own. */
export const walletFieldTranslationSources = (program: Program | null): { field: string; value: string }[] => {
  if (!program) return [];

  const titles = (wallet: Partial<WalletConfig> | undefined, prefix: string): { field: string; value: string }[] =>
    (prefix ? (["ios"] as const) : (["ios", "android"] as const))
      .map(platform => ({ field: `${prefix}${platform}.title`, value: wallet?.[platform]?.title }))
      .filter((source): source is { field: string; value: string } => !!source.value?.trim());

  const sources = (wallet: Partial<WalletConfig> | undefined, prefix: string): { field: string; value: string }[] => [
    ...titles(wallet, prefix),
    ...(Object.keys(FRONT_SLOTS) as WalletFrontPlatform[]).flatMap(platform =>
      FRONT_SLOTS[platform].flatMap(slot =>
        PARTS.filter(part => isWalletFieldPartTranslatable(platform, slot, part))
          .map(part => ({ field: `${prefix}${PLATFORM_PATHS[platform]}.${slot}.${part}`, value: fieldsOf(wallet, platform)[slot]?.[part] }))
          .filter((source): source is { field: string; value: string } => !!source.value?.trim()),
      ),
    ),
  ];

  return [
    ...sources(program.configurations.wallet, ""),
    ...Object.entries(program.configurations.tiers ?? {}).flatMap(([tier, wallet]) => sources(wallet as Partial<WalletConfig>, `tiers.${tier}.`)),
  ];
};
